/**
 * Team Inbox — chip set (UI Spec §7.1 "Chips row").
 *
 * Number-role chip (colour from the registry — N-number ready), owner
 * chip ("You" / name / red "Unassigned" — rendered ONLY when the Phase-2
 * ownership field exists), and the state chips (Triage · AI replied ·
 * Human replying · project ref).
 */
import type { InboxConversation, WaNumber } from '../lib/types';
import { aiLabelShort, isPromoEmail } from '../lib/emailStore';

/**
 * Channel chip — blue "✉ Email" / green "✆ WhatsApp" (spec PART 3.1). The WA
 * chip only earns its place in the mixed "All" lens; email rows always carry
 * the email chip so their identity is unmistakable.
 */
export function ChannelChip({ channel }: { channel: 'whatsapp' | 'email' }) {
  if (channel === 'email') {
    return <span className="ti-chip ch-em">✉ Email</span>;
  }
  return <span className="ti-chip ch-wa">✆ WhatsApp</span>;
}

/** Email-row chips: channel · known/triage · AI label · assignee. */
export function EmailStateChips({
  c,
  myUid,
}: {
  c: InboxConversation;
  myUid: string | null;
}) {
  const known = !!c.email?.knownContactId;
  return (
    <>
      <ChannelChip channel="email" />
      {known ? (
        <span className="ti-chip known">Known contact</span>
      ) : (
        <span className="ti-chip triage">Triage</span>
      )}
      {c.email && (
        <span className="ti-chip ai">AI: {aiLabelShort(c.email.aiLabel)}</span>
      )}
      <OwnerChip owner={c.owner} myUid={myUid} />
    </>
  );
}

/** Number-role chip — colour + label straight from the registry entry. */
export function NumberChip({ number }: { number: WaNumber | null }) {
  if (!number) return null; // legacy row (no receiving id) → no chip
  return (
    <span
      className="ti-chip num"
      style={{ color: number.colour }}
      title={`Received on ${number.line} (${number.label})`}
    >
      {number.label}
    </span>
  );
}

/**
 * Owner chip. `owner === undefined` → field not on the row yet (pre-V2
 * data) → render NOTHING (tolerate-absent rule). `null` → red Unassigned.
 */
export function OwnerChip({
  owner,
  myUid,
}: {
  owner: InboxConversation['owner'];
  myUid: string | null;
}) {
  if (owner === undefined) return null;
  if (owner === null) {
    return <span className="ti-chip owner unassigned">Unassigned</span>;
  }
  const label = myUid && owner.uid === myUid ? 'You' : owner.name;
  return <span className="ti-chip owner">{label}</span>;
}

/** Triage / AI / human-takeover / project state chips for one row. */
export function StateChips({ c }: { c: InboxConversation }) {
  return (
    <>
      {c.triage && <span className="ti-chip triage">Triage</span>}
      {c.aiBot && <span className="ti-chip ai">AI bot · internal</span>}
      {!c.aiBot && c.aiHandled && <span className="ti-chip ai">AI replied</span>}
      {c.aiPaused && <span className="ti-chip human">Human replying</span>}
      {c.projectRef && <span className="ti-chip proj">{c.projectRef}</span>}
    </>
  );
}

/**
 * Initials avatar. WhatsApp: tint follows the number's role colour (triage =
 * red). Email: solid blue, or solid red for an unmatched promo/automated
 * sender (AD-021 vibrant avatars).
 */
export function Avatar({
  c,
  label,
}: {
  c: InboxConversation;
  /**
   * Override for the initials. The row's own `initials` are computed from the
   * SERVER row, which does not know about the client-side contact match — so a
   * known contact whose row name is still a raw number showed "+91" next to
   * "Narsingh Agrawal · C00524" (owner report, 2026-08-02). The list passes the
   * name it actually renders.
   */
  label?: string;
}) {
  if (c.channel === 'email') {
    const known = !!c.email?.knownContactId;
    const promo = c.email ? isPromoEmail(c.email.aiLabel) : false;
    const cls = promo && !known ? 'email-triage' : 'email';
    return (
      <span className={`ti-av ${cls}`} aria-hidden>
        {label || c.initials}
      </span>
    );
  }
  const tint = c.triage
    ? 'var(--ti-triage)'
    : c.number?.colour || 'var(--ink-mute)';
  return (
    <span className="ti-av" style={{ background: tint }} aria-hidden>
      {c.aiBot ? '🤖' : label || c.initials}
    </span>
  );
}
